import { useState } from "react";
import { format, startOfToday, startOfWeek, startOfMonth, startOfYear, endOfToday, endOfWeek, endOfMonth, endOfYear } from "date-fns";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FileText, FileSpreadsheet, Download, ChevronRight, ChevronLeft, Zap, Loader2 } from "lucide-react";
import { generatePDF, generateExcel } from "@/lib/reports";
import { Product } from "@shared/schema";
import { useData } from "@/contexts/DataContext";
import { useToast } from "@/hooks/use-toast";

interface ReportDialogProps {
  transactions: any[];
  products: Product[];
}

type DateRangeOption = 'today' | 'week' | 'month' | 'year' | 'custom';
type Step = 'date' | 'format';

const REPORT_DOWNLOAD_COST = 500;

export function ReportDialog({ transactions, products }: ReportDialogProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('date');
  const [selectedOption, setSelectedOption] = useState<DateRangeOption>('today');
  const [customStartDate, setCustomStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [customEndDate, setCustomEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [isGenerating, setIsGenerating] = useState(false);

  // Added saveReportRecord to the destructuring below
  const { businessProfile, userMode, rewardPoints, updateRewardPoints, saveReportRecord } = useData();
  const { toast } = useToast();

  const isNormalUser = userMode === 'normal';
  const hasEnoughPoints = !isNormalUser || rewardPoints >= REPORT_DOWNLOAD_COST;

  const handleNext = () => {
    if (!hasEnoughPoints) {
      toast({
        title: "Insufficient Reward Points",
        description: `You need ${REPORT_DOWNLOAD_COST} points to download reports. You currently have ${rewardPoints} points.`,
        variant: "destructive",
      });
      return;
    }

    if (selectedOption === 'custom') {
      if (!customStartDate || !customEndDate) {
        toast({
          title: "Incomplete Selection",
          description: "Please select a start and end date for the custom range.",
          variant: "destructive",
        });
        return;
      }
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      if (start > end) {
        toast({
          title: "Invalid Range",
          description: "Start date cannot be after end date.",
          variant: "destructive",
        });
        return;
      }
    }
    setStep('format');
  };

  const handleBack = () => {
    setStep('date');
  };

  const handleGenerate = async (type: 'PDF' | 'Excel') => {
    if (isNormalUser) {
      if (rewardPoints < REPORT_DOWNLOAD_COST) {
        toast({
          title: "Insufficient Points",
          description: "You no longer have enough points to complete this download.",
          variant: "destructive",
        });
        setStep('date');
        return;
      }

      try {
        await updateRewardPoints(-REPORT_DOWNLOAD_COST, `Downloaded ${type} Report`, "report_download");
      } catch (err) {
        toast({
          title: "Error",
          description: "Failed to deduct reward points. Please try again.",
          variant: "destructive",
        });
        return;
      }
    }

    setIsGenerating(true);

    let start = new Date();
    let end = new Date();

    switch (selectedOption) {
      case 'today':
        start = startOfToday();
        end = endOfToday();
        break;
      case 'week':
        start = startOfWeek(new Date(), { weekStartsOn: 1 });
        end = endOfWeek(new Date(), { weekStartsOn: 1 });
        break;
      case 'month':
        start = startOfMonth(new Date());
        end = endOfMonth(new Date());
        break;
      case 'year':
        start = startOfYear(new Date());
        end = endOfYear(new Date());
        break;
      case 'custom':
        start = new Date(customStartDate);
        start.setHours(0, 0, 0, 0);
        end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999);
        break;
    }

    const filteredTransactions = transactions.filter((t) => {
      const date = new Date(t.createdAt || t.timestamp);
      return date >= start && date <= end;
    });

    let productSummaries = undefined;
    if (!isNormalUser) {
      const productMap = new Map<string, { name: string, quantity: number, value: number, unit: string }>();

      filteredTransactions.forEach(t => {
        if (t.type === 'sale' || t.type === 'SELL') {
          const items = t.items || (t.productName ? [{
            productId: t.productId,
            productName: t.productName,
            quantity: t.quantity,
            totalAmount: t.total
          }] : []);

          items.forEach((item: any) => {
            const existing = productMap.get(item.productName) || {
              name: item.productName,
              quantity: 0,
              value: 0,
              unit: products.find(p => p.name === item.productName)?.unit || 'pcs'
            };
            existing.quantity += Number(item.quantity || 0);
            existing.value += Number(item.totalAmount || 0);
            productMap.set(item.productName, existing);
          });
        }
      });
      productSummaries = Array.from(productMap.values());
    }

    const reportData = {
      transactions: filteredTransactions,
      startDate: start,
      endDate: end,
      businessName: businessProfile?.businessName || "DailyHisaab Business",
      businessAddress: businessProfile?.businessAddress,
      businessPhone: businessProfile?.businessPhone,
      businessEmail: businessProfile?.businessEmail,
      businessLogo: businessProfile?.businessLogo,
      businessGstin: businessProfile?.gstin,
      productSummaries,
    };

    try {
      if (type === "PDF") {
        await generatePDF(reportData);
      } else {
        await generateExcel(reportData);
      }

      // --- SAVE RECORD TO HISTORY ---
      await saveReportRecord({
        type,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        rangeLabel: selectedOption === 'custom'
          ? `${format(start, "dd MMM")} - ${format(end, "dd MMM")}`
          : selectedOption.toUpperCase(),
      });

      toast({
        title: "Success",
        description: isNormalUser
          ? `${type} Report saved to Documents. ${REPORT_DOWNLOAD_COST} points deducted.`
          : `${type} Report saved to Documents successfully.`,
      });

      setOpen(false);
      setTimeout(() => setStep('date'), 300);
    } catch (error: any) {
      toast({
        title: "Download Failed",
        description: error.message || "Could not save the report. Please allow storage permissions.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const options: { key: DateRangeOption, label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'year', label: 'This Year' },
    { key: 'custom', label: 'Custom' },
  ];

  return (
    <Dialog open={open} onOpenChange={(val) => {
      setOpen(val);
      if (!val) setTimeout(() => setStep('date'), 300);
    }}>
      <DialogTrigger asChild>
        <button className="bg-[#991B1B] hover:bg-[#7f1616] transition-colors text-white px-4 py-2 rounded-full font-bold text-sm flex items-center justify-center shadow-md active:scale-95">
          <Download className="w-4 h-4 mr-2" strokeWidth={3} /> Report
        </button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[425px] rounded-[32px] border-none shadow-2xl bg-[#FFFDF5] dark:bg-slate-900 p-6">
        <DialogTitle className="text-2xl font-extrabold text-slate-900 dark:text-white">
          Download Report
        </DialogTitle>

        <div className="space-y-5 mt-2">
          {step === 'date' ? (
            <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-200">
              <p className="text-sm font-bold tracking-widest text-slate-400 uppercase">Step 1: Select a date range</p>

              <div className="grid grid-cols-2 gap-3">
                {options.map(option => (
                  <button
                    key={option.key}
                    onClick={() => setSelectedOption(option.key)}
                    className={`flex items-center gap-2.5 px-4 py-3.5 rounded-2xl text-sm font-bold transition-all border-2 ${selectedOption === option.key
                      ? 'bg-[#8B1A1A] text-white border-[#8B1A1A]'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-100 dark:border-slate-700'
                      }`}
                  >
                    <div className={`w-2.5 h-2.5 rounded-full ${selectedOption === option.key ? 'bg-white' : 'bg-slate-300 dark:bg-slate-600'}`} />
                    {option.label}
                  </button>
                ))}
              </div>

              {selectedOption === 'custom' && (
                <div className="grid grid-cols-2 gap-4 pt-1 animate-in zoom-in-95 duration-200">
                  <div className="space-y-1.5">
                    <label htmlFor="startDate" className="text-xs font-bold tracking-widest text-slate-400 uppercase">Start Date</label>
                    <input
                      type="date"
                      id="startDate"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="flex h-12 w-full rounded-2xl border-2 border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1 text-sm font-bold text-slate-700 dark:text-slate-200 focus:border-emerald-500 outline-none transition-all"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="endDate" className="text-xs font-bold tracking-widest text-slate-400 uppercase">End Date</label>
                    <input
                      type="date"
                      id="endDate"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="flex h-12 w-full rounded-2xl border-2 border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1 text-sm font-bold text-slate-700 dark:text-slate-200 focus:border-emerald-500 outline-none transition-all"
                    />
                  </div>
                </div>
              )}

              <button
                className="w-full py-4 rounded-2xl text-lg font-bold text-white shadow-lg shadow-red-900/20 flex items-center justify-center gap-2"
                style={{ background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" }}
                onClick={handleNext}
              >
                Choose Format
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <div className="space-y-5 animate-in fade-in slide-in-from-left-4 duration-200">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm font-bold tracking-widest text-slate-400 uppercase">Step 2: Select format</p>
                {isNormalUser && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 shadow-sm animate-pulse">
                    <Zap className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                    {REPORT_DOWNLOAD_COST} Pts will be debited
                  </div>
                )}
              </div>

              <div className="grid gap-3">
                <button
                  disabled={isGenerating}
                  className="flex items-center justify-between w-full h-[72px] px-5 rounded-2xl border-2 border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 transition-all active:scale-[0.98] disabled:opacity-50"
                  onClick={() => handleGenerate("PDF")}
                >
                  <div className="flex items-center gap-4">
                    <div className="bg-red-50 dark:bg-red-900/20 p-2.5 rounded-xl text-red-600 dark:text-red-400">
                      <FileText className="h-6 w-6" />
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-slate-800 dark:text-white">PDF Report</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500">Professional document with branding</p>
                    </div>
                  </div>
                  {isGenerating ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : <Download className="h-5 w-5 text-slate-300 dark:text-slate-600" />}
                </button>

                <button
                  disabled={isGenerating}
                  className="flex items-center justify-between w-full h-[72px] px-5 rounded-2xl border-2 border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 transition-all active:scale-[0.98] disabled:opacity-50"
                  onClick={() => handleGenerate("Excel")}
                >
                  <div className="flex items-center gap-4">
                    <div className="bg-green-50 dark:bg-green-900/20 p-2.5 rounded-xl text-green-600 dark:text-green-400">
                      <FileSpreadsheet className="h-6 w-6" />
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-slate-800 dark:text-white">Excel Spreadsheet</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500">Data grid format for analysis</p>
                    </div>
                  </div>
                  {isGenerating ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : <Download className="h-5 w-5 text-slate-300 dark:text-slate-600" />}
                </button>
              </div>

              <button
                disabled={isGenerating}
                className="w-full py-3 rounded-2xl text-base font-bold text-slate-400 dark:text-slate-500 flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                onClick={handleBack}
              >
                <ChevronLeft className="h-4 w-4" />
                Back to Date Range
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}